import React from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

// Auth screens
import OnboardingScreen from '../screens/auth/OnboardingScreen';

// Main tabs
import MapScreen from '../screens/landmarks/MapScreen';
import LandmarksScreen from '../screens/landmarks/LandmarksScreen';
import RunsScreen from '../screens/runs/RunsScreen';
import ActiveRunScreen from '../screens/runs/ActiveRunScreen';
import RunHistoryScreen from '../screens/runs/RunHistoryScreen';
import RunDetailScreen from '../screens/runs/RunDetailScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import SettingsScreen from '../screens/profile/SettingsScreen';
import LogsScreen from '../screens/debug/LogsScreen';

// Landmark screens
import AddLandmarkScreen from '../screens/landmarks/AddLandmarkScreen';
import LandmarkDetailScreen from '../screens/landmarks/LandmarkDetailScreen';

// Run setup
import StartRunScreen from '../screens/runs/StartRunScreen';
import CreatePresetRunScreen from '../screens/runs/CreatePresetRunScreen';

import { useUserStore } from '../store';

export type AuthStackParamList = {
  Onboarding: undefined;
};

export type MapStackParamList = {
  Map: undefined;
  AddLandmark: { lat?: number; lng?: number } | undefined;
  LandmarkDetail: { landmarkId: string };
  Landmarks: undefined;
};

export type RunStackParamList = {
  Runs: undefined;
  StartRun: undefined;
  CreatePresetRun: undefined;
  ActiveRun: { mode: 'free' | 'preset'; presetRunId?: string };
  RunHistory: undefined;
  RunDetail: { runId: string };
};

export type ProfileStackParamList = {
  Profile: undefined;
  Settings: undefined;
  Logs: undefined;
};

export type TabParamList = {
  MapTab: undefined;
  RunTab: undefined;
  ProfileTab: undefined;
};

const AuthStack = createNativeStackNavigator<AuthStackParamList>();
const MapStack = createNativeStackNavigator<MapStackParamList>();
const RunStack = createNativeStackNavigator<RunStackParamList>();
const ProfileStack = createNativeStackNavigator<ProfileStackParamList>();
const Tab = createBottomTabNavigator<TabParamList>();

function MapStackNav() {
  return (
    <MapStack.Navigator>
      <MapStack.Screen name="Map" component={MapScreen} options={{ title: 'Map' }} />
      <MapStack.Screen name="AddLandmark" component={AddLandmarkScreen} options={{ title: 'Add Landmark' }} />
      <MapStack.Screen name="LandmarkDetail" component={LandmarkDetailScreen} options={{ title: 'Landmark' }} />
      <MapStack.Screen name="Landmarks" component={LandmarksScreen} options={{ title: 'My Landmarks' }} />
    </MapStack.Navigator>
  );
}

function RunStackNav() {
  return (
    <RunStack.Navigator>
      <RunStack.Screen name="Runs" component={RunsScreen} options={{ title: 'Runs' }} />
      <RunStack.Screen name="StartRun" component={StartRunScreen} options={{ title: 'Start Run' }} />
      <RunStack.Screen name="CreatePresetRun" component={CreatePresetRunScreen} options={{ title: 'Create Preset Run' }} />
      <RunStack.Screen name="ActiveRun" component={ActiveRunScreen} options={{ title: 'Running', headerShown: false }} />
      <RunStack.Screen name="RunHistory" component={RunHistoryScreen} options={{ title: 'Run History' }} />
      <RunStack.Screen name="RunDetail" component={RunDetailScreen} options={{ title: 'Run Detail' }} />
    </RunStack.Navigator>
  );
}

function ProfileStackNav() {
  return (
    <ProfileStack.Navigator>
      <ProfileStack.Screen name="Profile" component={ProfileScreen} options={{ title: 'Profile' }} />
      <ProfileStack.Screen name="Settings" component={SettingsScreen} options={{ title: 'Settings' }} />
      <ProfileStack.Screen name="Logs" component={LogsScreen} options={{ title: 'Activity Logs' }} />
    </ProfileStack.Navigator>
  );
}

function MainTabs() {
  return (
    <Tab.Navigator screenOptions={{ headerShown: false }}>
      <Tab.Screen name="MapTab" component={MapStackNav} options={{ title: 'Map', tabBarTestID: 'tab-map' }} />
      <Tab.Screen name="RunTab" component={RunStackNav} options={{ title: 'Run', tabBarTestID: 'tab-run' }} />
      <Tab.Screen name="ProfileTab" component={ProfileStackNav} options={{ title: 'Profile', tabBarTestID: 'tab-profile' }} />
    </Tab.Navigator>
  );
}

export default function AppNavigator() {
  const profile = useUserStore((s) => s.profile);

  return (
    <NavigationContainer>
      {profile ? (
        <MainTabs />
      ) : (
        <AuthStack.Navigator screenOptions={{ headerShown: false }}>
          <AuthStack.Screen name="Onboarding" component={OnboardingScreen} />
        </AuthStack.Navigator>
      )}
    </NavigationContainer>
  );
}
